// Contenitore UNICO delle timeline (osservazioni/previsioni + radar/nowcast):
// quando sono aperte insieme si impilano in un solo riquadro con UNA sola ✕
// (prima erano due box separati, la ✕ stava solo su uno e si sovrapponeva).
// I due dock (entrambi .wsv2-nt) vengono agganciati qui invece che al body; la
// visibilità dello stack segue quella dei figli, e la ✕ li chiude tutti finché
// il contesto non li ri-attiva.

let stack = null;
// "Timeline nascosta": la fonte unica è il toggle "Timeline" nel menu
// Visualizzazione (persistito da persist.js come gli altri). La ✕ lo spegne.
let stackClosed = false;
let observer = null;
let sizeObserver = null;
let glassMap = null;
let glassCanvas = null;

// Safari can corrupt backdrop-filter when its backdrop is a WebGL canvas.
// Copy only the small area below the dock, during the valid render frame;
// filtering this ordinary canvas leaves the map's GPU surface untouched.
export function initTimelineGlass(map) {
    if (!document.documentElement.classList.contains('ws-reduce-blur')) return;
    glassMap = map;
    let lastPaint = 0;
    let repaintTimer = null;
    map.on('render', () => {
        if (!stack || stack.hidden || document.hidden) return;
        const now = performance.now();
        if (now - lastPaint < 100) {
            if (!repaintTimer) repaintTimer = setTimeout(() => {
                repaintTimer = null;
                map.triggerRepaint();
            }, 100 - (now - lastPaint));
            return;
        }
        lastPaint = now;
        if (!glassCanvas) {
            glassCanvas = document.createElement('canvas');
            glassCanvas.className = 'wsv2-tl-glass';
            glassCanvas.setAttribute('aria-hidden', 'true');
            stack.prepend(glassCanvas);
        }
        const frame = map.getCanvas();
        const source = frame.getBoundingClientRect();
        const bounds = stack.getBoundingClientRect();
        const width = Math.ceil(bounds.width / 8);
        const height = Math.ceil(bounds.height / 8);
        if (glassCanvas.width !== width) glassCanvas.width = width;
        if (glassCanvas.height !== height) glassCanvas.height = height;
        const ctx = glassCanvas.getContext('2d');
        const rx = frame.width / source.width;
        const ry = frame.height / source.height;
        ctx.drawImage(frame, (bounds.left - source.left) * rx,
            (bounds.top - source.top) * ry, bounds.width * rx, bounds.height * ry,
            0, 0, width, height);
        const pixels = ctx.getImageData(0, 0, width, height);
        let src = pixels.data;
        const weights = [1, 4, 6, 4, 1];
        for (let pass = 0; pass < 6; pass++) {
            const dest = new Uint8ClampedArray(src.length);
            const horizontal = pass % 2 === 0;
            for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
                const index = (y * width + x) * 4;
                for (let c = 0; c < 4; c++) {
                    let sum = 0;
                    for (let k = -2; k <= 2; k++) {
                        const sx = horizontal ? Math.max(0, Math.min(width - 1, x + k)) : x;
                        const sy = horizontal ? y : Math.max(0, Math.min(height - 1, y + k));
                        sum += src[(sy * width + sx) * 4 + c] * weights[k + 2];
                    }
                    dest[index + c] = sum / 16;
                }
            }
            src = dest;
        }
        pixels.data.set(src);
        ctx.putImageData(pixels, 0, 0);
    });
}

// Espone l'altezza reale della timeline come CSS var sul root, così la legenda
// (in basso a sinistra) può posizionarsi SOPRA di essa invece di finirci sotto
// su schermi stretti dove la timeline occupa tutta la larghezza. 0 se nascosta.
function publishHeight() {
    const h = (stack && !stack.hidden) ? Math.ceil(stack.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--wsv2-tl-h', h + 'px');
    glassMap?.triggerRepaint();
}

// API usata dal toggle del pannello.
export function setTimelineVisible(visible) {
    stackClosed = !visible;
    refresh();
}
export function isTimelineVisible() {
    return !stackClosed;
}

function ensureStack() {
    if (stack) return stack;
    stack = document.createElement('div');
    stack.className = 'wsv2-tl-stack';
    stack.hidden = true;
    stack.innerHTML = '<button class="wsv2-tl-close" title="Chiudi la timeline" aria-label="Chiudi">✕</button>';
    document.body.appendChild(stack);
    sizeObserver = new ResizeObserver(() => publishHeight());
    sizeObserver.observe(stack);
    stack.querySelector('.wsv2-tl-close').addEventListener('click', () => {
        // ✕ = spegni la timeline: nascondi subito + chiedi al pannello di
        // spegnere il toggle "Timeline" (che salva la preferenza).
        stackClosed = true;
        refresh();
        document.dispatchEvent(new CustomEvent('wsv2:timeline-close-request'));
    });
    return stack;
}

function refresh() {
    if (!stack) return;
    const docks = [...stack.querySelectorAll('.wsv2-nt')];
    docks.forEach(d => d.classList.remove('wsv2-tl-first', 'wsv2-tl-last'));
    // "visibile" in ordine di layout (order), non di DOM.
    const visible = docks.filter(d => !d.hidden)
        .sort((a, b) => (parseInt(a.style.order || '0', 10)) - (parseInt(b.style.order || '0', 10)));
    if (visible.length) {
        visible[0].classList.add('wsv2-tl-first');
        visible[visible.length - 1].classList.add('wsv2-tl-last');
    }
    stack.hidden = stackClosed || visible.length === 0;
    publishHeight();
}

// order: 0 = in alto (osservazioni/previsioni), 1 = sotto (radar/nowcast).
export function mountTimelineDock(dock, { order = 0 } = {}) {
    ensureStack();
    dock.style.order = String(order);
    if (dock.parentElement !== stack) stack.appendChild(dock);
    if (!observer) {
        observer = new MutationObserver(() => refresh());
    }
    observer.observe(dock, { attributes: true, attributeFilter: ['hidden'] });
    refresh();
    document.dispatchEvent(new CustomEvent('wsv2:timeline-dock-mounted', { detail: { dock, order } }));
}
